// Phase 2.12 Tier 3 Step 28 — Ask in English Stimulus controller.
//
// Two-step flow:
//   1. User types a question, hits "Ask AI". Controller POSTs to
//      `/ask_in_english/draft` and renders the returned (name,
//      dataset, columns, filters) preview inside the modal.
//   2. User clicks "Save as widget". Controller POSTs to
//      `/ask_in_english/save`, which creates a Report row, registers
//      it as a widget, and adds it to the user's dashboard layout.
//      Reload the page so the new widget renders.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    draftUrl: String,
    saveUrl: String,
    projectId: Number,
    csrfToken: String
  }

  static targets = ["modal", "input", "preview", "rationale", "saveButton", "errorBanner",
                    "translateButton", "translateIcon", "translateLabel"]

  open(event) {
    event?.preventDefault()
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden")
    setTimeout(() => this.hasInputTarget && this.inputTarget.focus(), 50)
  }

  close() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
    this._resetPreview()
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async draft(event) {
    event?.preventDefault()
    const question = (this.hasInputTarget ? this.inputTarget.value : "").trim()
    if (!question) {
      this._showError("Type a question first.")
      return
    }

    this._setLoading(true)
    try {
      const res = await fetch(this.draftUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          question: question,
          project_id: this._scopedProjectId()
        })
      })
      const data = await res.json()
      if (!data.success || data.error) {
        this._showError(data.rationale || data.error || "Failed to draft a report.")
        return
      }
      this._renderPreview(data)
    } catch (err) {
      this._showError("Network error.")
    } finally {
      this._setLoading(false)
    }
  }

  async save(event) {
    event?.preventDefault()
    if (!this._currentDraft) return

    this._setLoading(true)
    try {
      const res = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: this._jsonHeaders(),
        credentials: "same-origin",
        body: JSON.stringify({
          report: this._currentDraft.report,
          project_id: this._scopedProjectId()
        })
      })
      const data = await res.json()
      if (!data.success) {
        this._showError((data.errors || ["Save failed"]).join(", "))
        return
      }
      window.location.reload()
    } catch (err) {
      this._showError("Network error saving widget.")
    } finally {
      this._setLoading(false)
    }
  }

  _renderPreview(data) {
    this._currentDraft = data
    if (this.hasRationaleTarget) {
      this.rationaleTarget.textContent = data.rationale || ""
    }
    if (this.hasPreviewTarget) {
      const r = data.report || {}
      this.previewTarget.innerHTML = `
        <div class="text-[13px]">
          <div class="font-medium text-gray-900">${this._escape(r.name || "")}</div>
          <div class="text-gray-500 text-[11px] mt-1">Dataset: <code class="bg-gray-100 px-1 rounded">${this._escape(r.dataset_key || "")}</code></div>
          <div class="text-gray-500 text-[11px] mt-1">Columns: ${(r.column_keys || []).map((c) => `<code class="bg-gray-100 px-1 rounded">${this._escape(c)}</code>`).join(" ")}</div>
          ${(r.filters || []).length > 0 ? `<div class="text-gray-500 text-[11px] mt-1">Filters: ${(r.filters).map((f) => `${this._escape(f.column_key)} ${this._escape(f.operator)} ${this._escape(f.value)}`).join("; ")}</div>` : ""}
        </div>
      `
    }
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = false
  }

  _resetPreview() {
    this._currentDraft = null
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = ""
    if (this.hasRationaleTarget) this.rationaleTarget.textContent = ""
    if (this.hasErrorBannerTarget) this.errorBannerTarget.classList.add("hidden")
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true
    if (this.hasInputTarget) this.inputTarget.value = ""
  }

  _showError(message) {
    if (!this.hasErrorBannerTarget) return
    this.errorBannerTarget.textContent = message
    this.errorBannerTarget.classList.remove("hidden")
  }

  _setLoading(on) {
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = on
    this._setTranslateLoading(on)
  }

  // Mirrors Project Pulse's "thinking" treatment: swap the static
  // gradient icon + text for the rainbow shimmer animation, disable
  // the button so duplicate clicks can't fire while Gemini drafts.
  _setTranslateLoading(on) {
    if (!this.hasTranslateButtonTarget) return
    this.translateButtonTarget.disabled = on

    if (this.hasTranslateIconTarget) {
      this.translateIconTarget.classList.toggle("ai-gradient-icon", !on)
      this.translateIconTarget.classList.toggle("ai-rainbow-icon", on)
    }
    if (this.hasTranslateLabelTarget) {
      this.translateLabelTarget.classList.toggle("ai-gradient-text", !on)
      this.translateLabelTarget.classList.toggle("ai-rainbow-text", on)
      this.translateLabelTarget.textContent = on ? "Translating…" : "Translate"
    }
  }

  // The Stimulus Number-typed value coerces missing / blank attributes
  // to `0`, which the controller would treat as a real project_id and
  // 404 on lookup. Send `null` whenever the attribute renders blank
  // (portfolio scope) so `lookup_project` correctly returns nil.
  _scopedProjectId() {
    const raw = this.element.getAttribute("data-ask-in-english-project-id-value")
    if (raw === null || raw === "") return null
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : null
  }

  _jsonHeaders() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
    }
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
